"use client";

import { useState } from "react";
import Link from "next/link";
import { useSitePathname } from "@/components/site/site-prefix-context";
import { MenuIcon, XIcon } from "lucide-react";
import { useTranslations } from "next-intl";

import { SiteLanguageBanner } from "@/components/site/site-language-banner";
import { SiteLanguageSwitcher } from "@/components/site/site-language-switcher";
import { SiteSearchBox } from "@/components/site/site-search-box";
import { SiteUserMenu } from "@/components/site/site-user-menu";
import { resolveSiteLogoUrl } from "@/lib/settings/basic-page";
import type { SiteInfo, SiteNavItem } from "@/templates/site/types";

function isActive(pathname: string, href: string, homeHref = "/"): boolean {

  if (href === homeHref) {
    return pathname === homeHref;
  }

  return pathname === href || pathname.startsWith(href);
}

function isItemActive(pathname: string, item: SiteNavItem, homeHref: string): boolean {
  return (
    isActive(pathname, item.href, homeHref) ||
    (item.activePath ? isActive(pathname, item.activePath, homeHref) : false)
  );
}

function newWindowProps(item: SiteNavItem) {
  return item.newWindow ? { target: "_blank", rel: "noopener noreferrer" } : {};
}

export function SiteHeader({
  site,
  nav = [],
  overlay = false,
}: {
  site: SiteInfo;
  nav?: SiteNavItem[];

  overlay?: boolean;
}) {
  const t = useTranslations("site.common");
  const pathname = useSitePathname();
  const [menuOpen, setMenuOpen] = useState(false);

  const logoUrl = resolveSiteLogoUrl(site.logo);

  const homeHref = site.language?.homeHref ?? "/";
  const items: SiteNavItem[] = [{ label: t("home"), href: homeHref }, ...nav];

  return (
    <header

      className={
        overlay
          ? "fixed inset-x-0 top-0 z-40 border-b border-border/25 bg-background/45 backdrop-blur-[18px] backdrop-saturate-150"
          : "sticky top-0 z-40 border-b border-border/25 bg-background/45 backdrop-blur-[18px] backdrop-saturate-150"
      }

      style={{ height: 65 }}
    >
      <div className="flex h-full w-full items-center gap-6 px-4 sm:px-6">
        <Link href={homeHref} className="flex shrink-0 items-center">
          {logoUrl ? (

            // eslint-disable-next-line @next/next/no-img-element
            <img
              className="block h-[34px] w-auto max-w-[132px] object-contain object-left"
              src={logoUrl}
              alt={site.name}
            />
          ) : (
            <span className="font-display text-xl font-semibold tracking-tight text-foreground">
              {site.shortName}
            </span>
          )}
        </Link>

        <nav className="hidden min-w-0 flex-1 items-center justify-center gap-8 md:flex">
          {items.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              aria-current={isItemActive(pathname, item, homeHref) ? "page" : undefined}
              {...newWindowProps(item)}
              className={`whitespace-nowrap py-2 text-[15px] leading-tight transition-colors hover:text-brand ${
                isItemActive(pathname, item, homeHref)
                  ? "text-brand"
                  : "text-foreground/85"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-2 md:ml-0 sm:gap-3">
          {site.searchEnabled === false ? null : (
            <SiteSearchBox prefix={site.language?.prefix ?? ""} />
          )}
          {site.language ? <SiteLanguageSwitcher language={site.language} /> : null}
          <SiteUserMenu />

          <button
            type="button"
            aria-label={t("menu")}
            aria-expanded={menuOpen}
            className="flex size-10 items-center justify-center rounded-lg text-foreground md:hidden"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? (
              <XIcon className="size-5" />
            ) : (
              <MenuIcon className="size-5" />
            )}
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div className="border-t border-border/40 bg-background/95 px-4 py-3 md:hidden">
          <nav className="flex flex-col">
            {items.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                aria-current={isItemActive(pathname, item, homeHref) ? "page" : undefined}
                {...newWindowProps(item)}

                onClick={() => setMenuOpen(false)}
                className={`py-2.5 text-[15px] ${
                  isItemActive(pathname, item, homeHref)
                    ? "text-brand"
                    : "text-foreground/85"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      ) : null}

      {site.language ? <SiteLanguageBanner language={site.language} /> : null}
    </header>
  );
}
